import React, { FC, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import Input from "shared/Input/Input";
import Textarea from "shared/Textarea/Textarea";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import toast from "react-hot-toast";
import { ContactFun } from "api/profile/profile";
import { useData } from "data/data-provider";
import FAQ from "designs/DesignA/components/FAQs/FAQs";

export interface PageContactProps {
  className?: string;
  asSection?: boolean;
}

/**
 * Design A — Contact page.
 *
 * Every text + contact field is sourced from FMS data:
 *  - Heading / subtitle fall back through company name + SEO title.
 *  - Address / phone / email come from `companyDetails`. Rows where the
 *    field is empty are dropped so we never render a "N/A" placeholder.
 *  - Form posts to `/api/company/contact-us/` with the active company id.
 *
 * The form supports basic validation (required fields + email shape) and
 * shows toasts on success / failure. Inputs are kept in local state and
 * cleared on a successful POST.
 */
const PageContact: FC<PageContactProps> = ({
  className = "",
  asSection = false,
}) => {
  const { companyDetails } = useData();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  // ---- copy ----------------------------------------------------------
  // Use FMS-managed copy where it exists, fall back to a sensible default.
  // The company name is used for the page title so search engines and
  // browser tabs read the tenant brand, not a generic "Contact" string.
  const companyName: string =
    (companyDetails?.company_name || "").trim() ||
    (companyDetails?.company?.name || "").trim() ||
    "";
  const pageTitle = companyName ? `Contact ${companyName}` : "Contact";
  const heading = companyName
    ? `Have a question about ${companyName}?`
    : "Have a question? Get in touch.";
  const subheading =
    "Send us a message and the team will reply within one business day.";

  // ---- contact rows --------------------------------------------------
  // Map raw companyDetails fields to a list of {icon, label, detail, href?}.
  // Entries with empty detail are filtered out so the layout collapses
  // cleanly when a tenant hasn't filled in a particular field yet.
  const contacts = useMemo(() => {
    const rows: {
      id: string;
      icon: React.ReactNode;
      label: string;
      detail?: string;
      href?: string;
    }[] = [];

    if (companyDetails?.address) {
      rows.push({
        id: "address",
        label: "Office Location",
        detail: companyDetails.address,
        icon: (
          <svg
            width="18"
            height="18"
            viewBox="0 0 14 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="text-primary-300"
            aria-hidden
          >
            <path
              d="M12.3291 3.08238C11.2367 1.19716 9.29636 0.0454051 7.13864 0.0013895C7.04653 -0.000508942 6.95383 -0.000508942 6.86168 0.0013895C4.704 0.0454051 2.76366 1.19716 1.67121 3.08238C0.554581 5.00936 0.52403 7.32405 1.58948 9.2742L6.05302 17.4441C6.05502 17.4477 6.05703 17.4513 6.0591 17.4549C6.25548 17.7962 6.60729 18 7.00023 18C7.39314 18 7.74495 17.7962 7.9413 17.4549C7.94337 17.4513 7.94537 17.4477 7.94738 17.4441L12.4109 9.2742C13.4763 7.32405 13.4457 5.00936 12.3291 3.08238ZM7.00016 8.15623C5.60442 8.15623 4.46891 7.02072 4.46891 5.62498C4.46891 4.22925 5.60442 3.09373 7.00016 3.09373C8.3959 3.09373 9.53141 4.22925 9.53141 5.62498C9.53141 7.02072 8.39594 8.15623 7.00016 8.15623Z"
              fill="currentColor"
            />
          </svg>
        ),
      });
    }

    if (companyDetails?.company_phone) {
      const phone = String(companyDetails.company_phone).trim();
      rows.push({
        id: "phone",
        label: "Phone",
        detail: phone,
        href: `tel:${phone.replace(/[^+\d]/g, "")}`,
        icon: (
          <svg
            width="18"
            height="18"
            viewBox="0 0 16 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="text-primary-300"
            aria-hidden
          >
            <path
              d="M15.2947 14.3934C15.3734 14.4945 15.4289 14.6037 15.4604 14.7211C15.5039 14.8837 15.5014 15.0464 15.4526 15.2107C15.4041 15.3744 15.3118 15.5146 15.1763 15.6306L14.5373 16.1685C14.2869 16.3745 14.0266 16.5452 13.7565 16.6805C13.4863 16.8158 13.2053 16.9228 12.913 17.0011C12.443 17.1271 11.9579 17.1733 11.4573 17.1399C10.9568 17.1066 10.458 17.0003 9.96106 16.8216C9.46412 16.6428 8.97654 16.396 8.49896 16.0817C8.02126 15.767 7.5689 15.3948 7.14261 14.9644C6.88214 14.7323 6.60174 14.463 6.30099 14.1565C6.04587 13.8953 5.74995 13.5799 5.41271 13.2099C5.07558 12.8403 4.72397 12.4235 4.35738 11.9608C3.98561 11.4991 3.67933 11.091 3.43896 10.7362C3.19848 10.381 3.00618 10.084 2.8629 9.8449C2.69529 9.5657 2.56626 9.32234 2.47591 9.11523C2.24941 8.74634 2.05474 8.37119 1.8923 7.98966C1.72986 7.60813 1.59674 7.22281 1.49263 6.83426C1.35649 6.32618 1.27666 5.82383 1.25249 5.32649C1.22832 4.82915 1.26788 4.35003 1.37033 3.88938C1.47278 3.42873 1.64325 2.99556 1.88175 2.58989C2.12025 2.18422 2.43383 1.82226 2.82227 1.50316L3.45396 0.97856C3.55455 0.899549 3.66213 0.84492 3.77617 0.814365C3.93666 0.771361 4.09805 0.775646 4.26046 0.827643C4.42244 0.879752 4.55927 0.974815 4.67021 1.11348L6.90043 3.89053C6.97955 3.99155 7.03418 4.09914 7.06474 4.21317C7.10966 4.38084 7.10812 4.54738 7.05967 4.71291C7.01123 4.87844 6.91823 5.01789 6.78013 5.13094L5.53035 6.16836C5.36518 6.30541 5.25511 6.4707 5.19952 6.66531C5.14435 6.8598 5.14242 7.05361 5.19425 7.24704C5.23363 7.39402 5.29809 7.52477 5.38816 7.6396L9.16027 12.3565C9.30703 12.5376 9.47383 12.655 9.6615 12.7083C9.84875 12.7618 10.0379 12.7632 10.2279 12.7123C10.3576 12.6775 10.4819 12.6125 10.6009 12.5177L11.8506 11.4803C11.9501 11.4021 12.0567 11.3472 12.1707 11.3167C12.3279 11.2746 12.4884 11.2791 12.6521 11.3308C12.8158 11.3824 12.954 11.4762 13.0666 11.6126L15.2947 14.3934Z"
              fill="currentColor"
            />
          </svg>
        ),
      });
    }

    if (companyDetails?.company_email) {
      const mail = String(companyDetails.company_email).trim();
      rows.push({
        id: "email",
        label: "Email",
        detail: mail,
        href: `mailto:${mail}`,
        icon: (
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="text-primary-300"
            aria-hidden
          >
            <path
              d="M16.418 2.67188H1.58207C1.33855 2.67188 1.11056 2.73175 0.904297 2.83043L7.88151 9.7725C8.49843 10.3894 9.50165 10.3894 10.1186 9.7725L17.0958 2.83043C16.8895 2.73175 16.6615 2.67188 16.418 2.67188Z"
              fill="currentColor"
            />
            <path
              d="M0.158555 3.57594C0.0598711 3.7822 0 4.01019 0 4.25372V13.7459C0 13.9894 0.0598711 14.2175 0.158555 14.4237L5.61758 8.99981L0.158555 3.57594Z"
              fill="currentColor"
            />
            <path
              d="M11.6368 9.74576L10.8643 10.5182C9.83634 11.5462 8.16371 11.5462 7.13578 10.5182L6.36332 9.74576L0.904297 15.1696C1.11056 15.2683 1.33855 15.3282 1.58207 15.3282H16.418C16.6615 15.3282 16.8895 15.2683 17.0958 15.1696L11.6368 9.74576Z"
              fill="currentColor"
            />
            <path
              d="M17.8418 3.57594L12.3828 8.99981L17.8418 14.4237C17.9405 14.2174 18.0004 13.9894 18.0004 13.7459V4.25372C18.0004 4.01019 17.9405 3.7822 17.8418 3.57594Z"
              fill="currentColor"
            />
          </svg>
        ),
      });
    }

    return rows;
  }, [companyDetails]);

  // ---- submit --------------------------------------------------------
  const sendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedMessage = message.trim();

    if (!trimmedName || !trimmedEmail || !trimmedMessage) {
      toast.error("Please fill in name, email, and message.");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    try {
      setLoading(true);
      const result = await ContactFun({
        company: companyDetails?.company,
        name: trimmedName,
        email: trimmedEmail,
        message: trimmedMessage,
      });
      if (result.error) {
        toast.error(result.error);
      } else {
        toast.success("Message sent — we'll be in touch shortly.");
        setName("");
        setEmail("");
        setMessage("");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`nc-PageContact overflow-hidden ${className}`}
      data-nc-id="PageContact"
    >
      {!asSection && (
        <Helmet>
          <title>{pageTitle}</title>
        </Helmet>
      )}

      <div className="container mx-auto py-12 sm:py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          {/* ----- Copy + contact rows ----- */}
          <div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-text-color leading-tight">
              {heading}
            </h1>
            <p className="mt-4 text-base sm:text-lg text-secondary-text-color leading-relaxed max-w-md">
              {subheading}
            </p>

            {contacts.length > 0 && (
              <ul className="mt-8 space-y-5">
                {contacts.map((row) => (
                  <li key={row.id} className="flex items-start gap-4">
                    <span
                      className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-secondary-50 ring-1 ring-secondary-100"
                      aria-hidden
                    >
                      {row.icon}
                    </span>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold uppercase tracking-wider text-secondary-text-color">
                        {row.label}
                      </p>
                      {row.href ? (
                        <a
                          href={row.href}
                          className="mt-1 block text-sm sm:text-base font-medium text-text-color hover:text-primary-300 break-words"
                        >
                          {row.detail}
                        </a>
                      ) : (
                        <p className="mt-1 text-sm sm:text-base font-medium text-text-color break-words whitespace-pre-line">
                          {row.detail}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* ----- Form ----- */}
          <div className="rounded-2xl bg-secondary-50 ring-1 ring-secondary-100 p-6 sm:p-8 shadow-sm">
            <form className="grid grid-cols-1 gap-5" onSubmit={sendEmail} noValidate>
              <label className="block">
                <span className="block text-sm font-semibold text-text-color mb-1.5">
                  Full name
                </span>
                <Input
                  placeholder="Your name"
                  type="text"
                  className="mt-0"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </label>
              <label className="block">
                <span className="block text-sm font-semibold text-text-color mb-1.5">
                  Email address
                </span>
                <Input
                  type="email"
                  placeholder="you@example.com"
                  className="mt-0"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </label>
              <label className="block">
                <span className="block text-sm font-semibold text-text-color mb-1.5">
                  Message
                </span>
                <Textarea
                  className="mt-0"
                  placeholder="How can we help?"
                  rows={6}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                />
              </label>
              <ButtonPrimary
                type="submit"
                className="min-w-[180px] justify-self-start"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <span className="spinner-border animate-spin inline-block w-5 h-5 border-4 rounded-full border-t-transparent border-secondary-100" />
                  </span>
                ) : (
                  "Send Message"
                )}
              </ButtonPrimary>
            </form>
          </div>
        </div>
      </div>

      <div className="container relative pb-16">
        <FAQ />
      </div>
    </div>
  );
};

export default PageContact;
